import { configureStore, createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { useSelector } from 'react-redux';
import { authApi } from '../services/api';

// ── Auth slice ──────────────────────────────────────────────────────────────
const authSlice = createSlice({
  name: 'auth',
  initialState: {
    user: null,
    token: localStorage.getItem('bg_access_token'),
    loading: false,
    error: null,
  },
  reducers: {
    logout: state => {
      state.user = null; state.token = null;
      localStorage.removeItem('bg_access_token');
      localStorage.removeItem('bg_refresh_token');
    },
    setUser: (state, action) => { state.user = action.payload; },
  },
  extraReducers: builder => {
    builder
      .addCase(loginThunk.pending, s => { s.loading = true; s.error = null; })
      .addCase(loginThunk.fulfilled, (s, a) => {
        s.loading = false;
        s.token = a.payload.accessToken;
        s.user = a.payload.user;
        localStorage.setItem('bg_access_token', a.payload.accessToken);
        if (a.payload.refreshToken) localStorage.setItem('bg_refresh_token', a.payload.refreshToken);
      })
      .addCase(loginThunk.rejected, (s, a) => {
        s.loading = false;
        s.error = a.error.message;
      });
  }
});

export const loginThunk = createAsyncThunk('auth/login', async ({ phone, password }) => {
  return authApi.login(phone, password);
});
export const { logout, setUser } = authSlice.actions;

// ── Modules slice ────────────────────────────────────────────────────────────
// Aktiv/deaktiv modul vəziyyəti — sidebar, dashboard, web hamısı buna baxır

// Canonical list of ALL modules with their defaults
export const DEFAULT_MODULES = [
  { key: 'barrier',       label: 'Şlaqbaum Sistemi',   icon: '🚧', isEnabled: true  },
  { key: 'restaurant',    label: 'Restoran & QR Menyu', icon: '🍽️', isEnabled: true  },
  { key: 'wallet',        label: 'Elektron Cüzdan',     icon: '💳', isEnabled: true  },
  { key: 'taxi',          label: 'Taksi Xidməti',       icon: '🚕', isEnabled: false },
  { key: 'delivery',      label: 'Çatdırılma',          icon: '📦', isEnabled: true  },
  { key: 'announcements', label: 'Elanlar',             icon: '📢', isEnabled: true  },
  { key: 'services',      label: 'Sistem Monitorinqi',  icon: '🖥️', isEnabled: true  },
];

const STORAGE_MODULES = 'bg_modules';

function mergeWithDefaults(saved) {
  // Always return a full list keyed by DEFAULT_MODULES, merging saved isEnabled values
  return DEFAULT_MODULES.map(def => {
    const found = Array.isArray(saved) ? saved.find(m => m.key === def.key) : null;
    return found ? { ...def, isEnabled: found.isEnabled } : def;
  });
}

function loadModulesFromStorage() {
  try {
    const raw = localStorage.getItem(STORAGE_MODULES);
    if (raw) return mergeWithDefaults(JSON.parse(raw));
  } catch { /* ignore */ }
  return [...DEFAULT_MODULES];
}

function saveModulesToStorage(list) {
  try {
    localStorage.setItem(STORAGE_MODULES, JSON.stringify(list));
  } catch { /* ignore */ }
}

const modulesSlice = createSlice({
  name: 'modules',
  initialState: {
    list: loadModulesFromStorage(),  // [{key, isEnabled, label, icon, ...}]
    loaded: false,
  },
  reducers: {
    setModules: (state, action) => {
      // Merge API response with defaults — ensures all keys always exist
      state.list = mergeWithDefaults(action.payload);
      state.loaded = true;
      saveModulesToStorage(state.list);
    },
    toggleModuleLocal: (state, action) => {
      const m = state.list.find(x => x.key === action.payload);
      if (m) {
        m.isEnabled = !m.isEnabled;
        saveModulesToStorage(state.list);
      }
    },
  },
});
export const { setModules, toggleModuleLocal } = modulesSlice.actions;

// Selector: returns full module list (always populated via DEFAULT_MODULES)
export const selectModules = s => s.modules?.list?.length ? s.modules.list : DEFAULT_MODULES;

// Selector factory: is a specific module key enabled?
export const selectModuleEnabled = (key) => (state) =>
  (state.modules?.list || DEFAULT_MODULES).find(m => m.key === key)?.isEnabled ?? true;

// Helper: check if a module key is enabled in a given modules array
export const isModuleEnabled = (modules, key) => {
  const m = (modules || DEFAULT_MODULES).find(m => m.key === key);
  return m ? m.isEnabled : true;
};

// Hook: modul aktiv olub olmadığını yoxla
export function useModuleEnabled(key) {
  const modules = useSelector(s => s.modules?.list || DEFAULT_MODULES);
  const mod = modules.find(m => m.key === key);
  return mod ? mod.isEnabled : true; // default: enabled
}

// ── i18n slice ──────────────────────────────────────────────────────────────
const STORAGE_LANG = 'bg_lang';
const i18nSlice = createSlice({
  name: 'i18n',
  initialState: { lang: localStorage.getItem(STORAGE_LANG) || 'az' },
  reducers: {
    setLang: (state, action) => {
      state.lang = action.payload;
      localStorage.setItem(STORAGE_LANG, action.payload);
    },
  },
});
export const { setLang } = i18nSlice.actions;

// ── Theme slice ──────────────────────────────────────────────────────────────
const STORAGE_THEME = 'bg_theme';
const themeSlice = createSlice({
  name: 'theme',
  initialState: { mode: localStorage.getItem(STORAGE_THEME) || 'dark' }, // 'dark' | 'light' | 'glass'
  reducers: {
    setTheme: (state, action) => {
      state.mode = action.payload;
      localStorage.setItem(STORAGE_THEME, action.payload);
      document.documentElement.setAttribute('data-theme', action.payload);
    },
  },
});
export const { setTheme } = themeSlice.actions;

// ── UI slice ─────────────────────────────────────────────────────────────────
const uiSlice = createSlice({
  name: 'ui',
  initialState: { sidebarOpen: true, activeModule: 'dashboard' },
  reducers: {
    toggleSidebar: s => { s.sidebarOpen = !s.sidebarOpen; },
    setModule: (s, a) => { s.activeModule = a.payload; },
  }
});
export const { toggleSidebar, setModule } = uiSlice.actions;

// ── Store ────────────────────────────────────────────────────────────────────
export const store = configureStore({
  reducer: {
    auth: authSlice.reducer,
    modules: modulesSlice.reducer,
    i18n: i18nSlice.reducer,
    theme: themeSlice.reducer,
    ui: uiSlice.reducer,
  },
});
